import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Loader2, Users, Layers3, Wallet, TrendingDown, ClipboardCheck, CalendarDays, AlertCircle, Info } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/utils/money";
import { formatSessionDate, formatSessionTime } from "@/utils/datetime";

type Summary = any;

export default function Dashboard() {
    const { t } = useTranslation();
    const { activeMembership, hasModuleAccess, me } = useAuth();
    const isPO = !!me?.user?.is_platform_owner;
    // Phase BUG-1: TAC helpers exposed to the render tree. Platform owners
    // bypass module gates (they need to see everything to support tenants);
    // for every other role we honour the same TAC toggles the sidebar uses
    // so widgets never link to a `module_disabled` screen.
    const canModule = (m: string) => isPO || hasModuleAccess(m, "view");
    const [data, setData] = useState<Summary | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try { const r = await api.get("/dashboard/summary"); setData(r.data); }
        catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load, activeMembership?.tenant.id]);

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div>
                <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("dashboard.title")}</h1>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{activeMembership?.tenant.name}</p>
            </div>
            {activeMembership?.tenant.is_demo && (
                <div className="rounded-md p-3 text-xs inline-flex items-center gap-2" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="demo-banner">
                    <AlertCircle size={14}/> {t("dashboard.demo_banner")}
                </div>
            )}
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="dashboard-error">{err}</div>}
            {loading ? <div className="p-12 flex justify-center"><Loader2 className="animate-spin" style={{ color: "var(--text-muted)" }}/></div> :
                data?.role_scope === "director" ? <DirectorView data={data} canModule={canModule}/> :
                data?.role_scope === "teacher" ? <TeacherView data={data} canModule={canModule}/> :
                data?.role_scope === "admin" ? <AdminView data={data} canModule={canModule}/> :
                <div className="surface-card p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>
            }
        </div>
    );
}

function Kpi({ label, value, sub, icon, testid, tooltip, empty }: { label: string; value: React.ReactNode; sub?: React.ReactNode; icon: React.ReactNode; testid: string; tooltip?: string; empty?: boolean }) {
    return (
        <div className="surface-card p-5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                <span style={{ color: "var(--accent)" }}>{icon}</span>
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>{label}</span>
                {tooltip && <span title={tooltip}><Info size={12} style={{ color: "var(--text-muted)" }}/></span>}
            </div>
            {empty ? (
                <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid={`${testid}-empty`}>—</div>
            ) : (
                <>
                    <div className="text-2xl font-semibold tabular-nums" style={{ color: "var(--text)" }}>{value}</div>
                    {sub && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{sub}</div>}
                </>
            )}
        </div>
    );
}

function DirectorView({ data, canModule }: { data: any; canModule: (m: string) => boolean }) {
    const { t } = useTranslation();
    const tz = data.tenant_timezone || "Asia/Baku";
    const showInvoices = canModule("finance.invoices");
    const showPayments = canModule("finance.payments");
    const showFinanceAny = canModule("finance") || showInvoices || showPayments;
    const showTasks = canModule("tasks");
    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="director-kpis">
                <Kpi testid="kpi-active-students" label={t("dashboard.kpi.active_students")} value={data.active_students} icon={<Users size={16}/>} sub={`+${data.new_enrollments_month} / −${data.withdrawals_month} ${t("dashboard.this_month")}`}/>
                <Kpi testid="kpi-utilization" label={t("dashboard.kpi.utilization")} tooltip={t("dashboard.kpi.utilization_tt") as string} value={data.utilization_pct != null ? `${data.utilization_pct}%` : "—"} sub={`${data.utilization_filled_total}/${data.utilization_capacity_total}`} icon={<Layers3 size={16}/>} empty={data.utilization_pct == null}/>
                <Kpi testid="kpi-attendance" label={t("dashboard.kpi.attendance")} tooltip={t("dashboard.kpi.attendance_tt") as string} value={data.attendance_pct_month != null ? `${data.attendance_pct_month}%` : "—"} sub={`${t("dashboard.sample_size")}: ${data.attendance_sample_size}`} icon={<ClipboardCheck size={16}/>} empty={data.attendance_pct_month == null}/>
                {showPayments && <Kpi testid="kpi-debt" label={t("dashboard.kpi.overdue_debt")} value={fmtMoney(data.overdue_debt_minor, "AZN")} icon={<TrendingDown size={16}/>} sub={<Link to="/finance/debt" className="hover:underline" style={{ color: "var(--accent)" }}>{t("dashboard.view_details")}</Link>}/>}
            </div>
            {showFinanceAny && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="director-finance">
                    {showInvoices && <Kpi testid="kpi-invoiced" label={t("dashboard.kpi.invoiced_month")} value={fmtMoney(data.invoiced_month_minor, "AZN")} icon={<Wallet size={16}/>} sub={<Link to="/finance/invoices" className="hover:underline" style={{ color: "var(--accent)" }}>{t("dashboard.view_details")}</Link>}/>}
                    {showPayments && <Kpi testid="kpi-collected" label={t("dashboard.kpi.collected_month")} value={fmtMoney(data.collected_month_minor, "AZN")} icon={<Wallet size={16}/>} sub={<Link to="/finance/payments" className="hover:underline" style={{ color: "var(--accent)" }}>{t("dashboard.view_details")}</Link>}/>}
                    {showPayments && data.other_income_month_minor != null && <Kpi testid="kpi-other-income" label={t("dashboard.kpi.other_income_month")} value={fmtMoney(data.other_income_month_minor, "AZN")} icon={<Wallet size={16}/>} sub={<Link to="/finance/other-income" className="hover:underline" style={{ color: "var(--accent)" }}>{t("dashboard.view_details")}</Link>}/>}
                    {showPayments && data.income_month_minor != null && <Kpi testid="kpi-total-income" label={t("dashboard.kpi.total_income_month")} tooltip={t("dashboard.kpi.total_income_tt") as string} value={fmtMoney(data.income_month_minor, "AZN")} icon={<Wallet size={16}/>}/>}
                    {showPayments && <Kpi testid="kpi-expenses" label={t("dashboard.kpi.expenses_month")} value={fmtMoney(data.expenses_month_minor, "AZN")} icon={<Wallet size={16}/>} sub={<Link to="/finance/cash" className="hover:underline" style={{ color: "var(--accent)" }}>{t("dashboard.view_details")}</Link>}/>}
                </div>
            )}
            <TodaySessions items={data.today_sessions} tz={tz}/>
            {showTasks && data.tasks && <TasksCard tasks={data.tasks}/>}
        </>
    );
}

function AdminView({ data, canModule }: { data: any; canModule: (m: string) => boolean }) {
    const { t } = useTranslation();
    const tz = data.tenant_timezone || "Asia/Baku";
    const showPayments = canModule("finance.payments");
    const showTasks = canModule("tasks");
    const showCRM = canModule("crm");
    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="admin-kpis">
                <Kpi testid="kpi-today-sessions-count" label={t("dashboard.kpi.today_sessions")} value={data.today_sessions.length} icon={<CalendarDays size={16}/>}/>
                <Kpi testid="kpi-waitlist" label={t("dashboard.kpi.waitlist")} value={data.waitlist_count} icon={<Users size={16}/>}/>
                {showCRM && <Kpi testid="kpi-followups" label={t("dashboard.kpi.followups")} value={data.followups_today?.length ?? 0} sub={data.followups_overdue_count ? <span style={{ color: "var(--error-fg)" }}>{data.followups_overdue_count} {t("dashboard.overdue")}</span> : undefined} icon={<AlertCircle size={16}/>}/>}
            </div>
            {showCRM && data.followups_today && data.followups_today.length > 0 && (
                <div className="surface-card overflow-hidden" data-testid="followups-panel">
                    <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
                        <span className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("dashboard.followups_today")}</span>
                        <Link to="/crm" className="text-xs hover:underline" style={{ color: "var(--accent)" }}>{t("dashboard.view_details")}</Link>
                    </div>
                    <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                        {data.followups_today.map((f: any) => (
                            <li key={f.id} className="p-3 flex items-center justify-between text-sm" data-testid={`followup-${f.id}`}>
                                <div>
                                    <span style={{ color: "var(--text)" }}>{f.first_name} {f.last_name}</span>
                                    {f.phone && <span className="ml-2 font-mono text-xs" style={{ color: "var(--text-muted)" }}>{f.phone}</span>}
                                </div>
                                <span className="text-xs font-mono" style={{ color: f.overdue ? "var(--error-fg)" : "var(--text-muted)" }}>{f.local_time}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {showPayments && (
                <div className="surface-card overflow-hidden" data-testid="overdue-top5">
                    <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
                        <span className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("dashboard.overdue_top5")}</span>
                        <Link to="/finance/debt" className="text-xs hover:underline" style={{ color: "var(--accent)" }}>{t("dashboard.view_details")}</Link>
                    </div>
                    {data.overdue_top5.length === 0 ? <div className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : (
                        <table className="w-full text-sm">
                            <tbody>
                                {data.overdue_top5.map((it: any) => (
                                    <tr key={it.invoice_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`overdue-${it.invoice_id}`}>
                                        <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--text)" }}>{it.invoice_number}</td>
                                        <td className="px-4 py-3"><Link to={`/people/${it.person_id}`} className="hover:underline" style={{ color: "var(--text)" }}>{it.person_name}</Link></td>
                                        <td className="px-4 py-3 text-right tabular-nums" style={{ color: "var(--error-fg)" }}>{fmtMoney(it.debt_minor, it.currency)}</td>
                                        <td className="px-4 py-3 text-right text-xs" style={{ color: "var(--text-muted)" }}>{it.days_overdue}d</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
            <TodaySessions items={data.today_sessions} tz={tz}/>
            {showTasks && data.tasks && <TasksCard tasks={data.tasks}/>}
        </>
    );
}

function TeacherView({ data, canModule: _canModule }: { data: any; canModule: (m: string) => boolean }) {
    const { t } = useTranslation();
    const tz = data.tenant_timezone || "Asia/Baku";
    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teacher-groups">
                {data.my_groups.length === 0 ? <div className="col-span-full surface-card p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : data.my_groups.map((g: any) => (
                    <Link key={g.id} to={`/groups/${g.id}`} className="surface-card p-5 block hover:shadow-md transition-shadow" data-testid={`teacher-group-${g.id}`}>
                        <div className="flex items-center gap-2 mb-3"><Layers3 size={16} style={{ color: "var(--accent)" }}/><span className="font-medium" style={{ color: "var(--text)" }}>{g.name}</span></div>
                        <div className="text-2xl font-semibold tabular-nums" style={{ color: "var(--text)" }}>{g.active_count} <span className="text-sm" style={{ color: "var(--text-muted)" }}>/ {g.capacity}</span></div>
                    </Link>
                ))}
            </div>
            <TodaySessions items={data.today_sessions} tz={tz}/>
            <div className="surface-card overflow-hidden" data-testid="teacher-pending">
                <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
                    <span className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("dashboard.pending_attendance")}</span>
                </div>
                {data.pending_attendance.length === 0 ? <div className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : (
                    <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                        {data.pending_attendance.map((s: any) => (
                            <li key={s.id} className="p-4 flex items-center justify-between" data-testid={`pending-${s.id}`}>
                                <span className="text-sm" style={{ color: "var(--text)" }}>{formatSessionDate(s.start_at, tz)} · {formatSessionTime(s.start_at, tz)}</span>
                                <Link to={`/groups/${s.group_id}`} className="text-xs hover:underline" style={{ color: "var(--accent)" }}>{t("attendance.mark")} →</Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </>
    );
}

function TasksCard({ tasks }: { tasks: { open_count: number; overdue_count: number; done_this_week: number; mine_open_count: number } }) {
    const { t } = useTranslation();
    return (
        <Link to="/tasks" className="surface-card block hover:shadow-md transition-shadow" data-testid="dashboard-tasks-card">
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
                <ClipboardCheck size={16} style={{ color: "var(--accent)" }}/>
                <span className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("dashboard.tasks.title")}</span>
                <span className="ml-auto text-xs" style={{ color: "var(--accent)" }}>{t("dashboard.view_details")} →</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-5">
                <div data-testid="dashboard-tasks-open">
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("dashboard.tasks.open")}</div>
                    <div className="text-2xl font-semibold tabular-nums" style={{ color: "var(--text)" }}>{tasks.open_count}</div>
                </div>
                <div data-testid="dashboard-tasks-overdue">
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("dashboard.tasks.overdue")}</div>
                    <div className="text-2xl font-semibold tabular-nums" style={{ color: tasks.overdue_count > 0 ? "var(--error-fg)" : "var(--text)" }}>{tasks.overdue_count}</div>
                </div>
                <div data-testid="dashboard-tasks-done-week">
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("dashboard.tasks.done_this_week")}</div>
                    <div className="text-2xl font-semibold tabular-nums" style={{ color: "var(--success-fg)" }}>{tasks.done_this_week}</div>
                </div>
                <div data-testid="dashboard-tasks-mine">
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("dashboard.tasks.mine_open")}</div>
                    <div className="text-2xl font-semibold tabular-nums" style={{ color: "var(--text)" }}>{tasks.mine_open_count}</div>
                </div>
            </div>
        </Link>
    );
}

function TodaySessions({ items, tz }: { items: any[]; tz: string }) {
    const { t } = useTranslation();
    return (
        <div className="surface-card overflow-hidden" data-testid="today-sessions">
            <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
                <span className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("dashboard.today_sessions")}</span>
            </div>
            {items.length === 0 ? <div className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="today-empty">{t("common.empty")}</div> : (
                <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                    {items.map((s: any) => (
                        <li key={s.id} className="p-4 flex items-center justify-between" data-testid={`session-today-${s.id}`}>
                            <div className="min-w-0">
                                <span className="text-sm font-mono" style={{ color: "var(--text)" }}>{formatSessionTime(s.start_at, tz)}–{formatSessionTime(s.end_at, tz)}</span>
                                {s.group_name && <span className="ml-3 text-sm font-medium" style={{ color: "var(--text)" }} data-testid={`session-today-group-${s.id}`}>{s.group_name}</span>}
                                <span className={`ml-3 status-pill ${s.status === "held" ? "status-success" : s.status === "cancelled" ? "status-error" : "status-info"}`}>{t(`session.status.${s.status}` as any)}</span>
                            </div>
                            <Link to={`/groups/${s.group_id}`} className="text-xs hover:underline" style={{ color: "var(--accent)" }}>→</Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
